import React, { useEffect, useRef, useState } from "react";
import { Button, Checkbox, Spin } from "antd";
import { FileOutlined, LoadingOutlined } from "@ant-design/icons";
import { DicomDataThumbnail } from "../../../models/DicomDataThumbnail/dicomDataThumbnail.model";
import { getSeriesDescriptionFromDicom } from "../../utils/getSeriesDescriptionFromDicom";
import "./caseSeriesExpandRow.scss";

interface CaseSeriesExpandRowProps {
  caseDetailId: number;
  fetchThumbnails: (
    caseDetailId: number,
    onSuccess: (thumbnails: DicomDataThumbnail[]) => void,
    onError: () => void,
    onFinal: () => void
  ) => void;
  fetchDicomFiles?: (caseDetailId: number, folder: string) => Promise<{ data: any[] }>;
  onSeriesOpen: (caseId: number, seriesIds: string[]) => void;
}

function CaseSeriesExpandRow({
  caseDetailId,
  fetchThumbnails,
  fetchDicomFiles,
  onSeriesOpen,
}: CaseSeriesExpandRowProps) {
  const [thumbnails, setThumbnails] = useState<DicomDataThumbnail[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [seriesNamesLoading, setSeriesNamesLoading] = useState(false);
  const fetchedFoldersRef = useRef<Set<string>>(new Set());
  const pendingSeriesNamesRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    setError(false);
    fetchThumbnails(
      caseDetailId,
      (data: DicomDataThumbnail[]) => {
        setThumbnails(data);
      },
      () => {
        setError(true);
      },
      () => {
        setLoading(false);
      }
    );
  }, [caseDetailId, fetchThumbnails]);

  useEffect(() => {
    fetchedFoldersRef.current = new Set();
    pendingSeriesNamesRef.current = 0;
    setSeriesNamesLoading(false);
  }, [caseDetailId]);

  useEffect(() => {
    if (!fetchDicomFiles || thumbnails.length === 0) return;
    thumbnails.forEach((series) => {
      if (
        series.folder &&
        !series.seriesDescription &&
        !fetchedFoldersRef.current.has(series.folder)
      ) {
        fetchedFoldersRef.current.add(series.folder);
        pendingSeriesNamesRef.current += 1;
        setSeriesNamesLoading(true);
        getSeriesDescriptionFromDicom(fetchDicomFiles, caseDetailId, series.folder)
          .then((desc) => {
            if (desc) {
              setThumbnails((prev) =>
                prev.map((t) =>
                  t.folder === series.folder ? { ...t, seriesDescription: desc } : t
                )
              );
            }
          })
          .finally(() => {
            pendingSeriesNamesRef.current -= 1;
            if (pendingSeriesNamesRef.current <= 0) {
              pendingSeriesNamesRef.current = 0;
              setSeriesNamesLoading(false);
            }
          });
      }
    });
  }, [caseDetailId, fetchDicomFiles, thumbnails]);

  if (loading) {
    return (
      <div className="case-series-expand-row case-series-expand-row--loading">
        <Spin indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />} />
        <span>Loading series...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="case-series-expand-row case-series-expand-row--error">
        Failed to load series
      </div>
    );
  }

  if (!thumbnails.length) {
    return (
      <div className="case-series-expand-row case-series-expand-row--empty">
        No series found
      </div>
    );
  }

  const toggleSelection = (folder: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(folder)) {
        next.delete(folder);
      } else {
        next.add(folder);
      }
      return next;
    });
  };

  const handleOpenSelected = () => {
    if (selectedIds.size > 0) {
      onSeriesOpen(caseDetailId, Array.from(selectedIds));
    }
  };

  return (
    <div className="case-series-expand-row">
      <div className="case-series-expand-row__actions">
        <Button
          type="primary"
          size="small"
          disabled={selectedIds.size === 0}
          onClick={handleOpenSelected}
        >
          View Selected ({selectedIds.size})
        </Button>
        {seriesNamesLoading && (
          <span className="case-series-expand-row__names-loading">
            <Spin size="small" indicator={<LoadingOutlined spin />} />
            Series name loading...
          </span>
        )}
      </div>
      <div className="case-series-expand-row__list">
        {thumbnails.map((series) => {
          const folderLastPart = series.folder?.split("/").pop();
          const displayName =
            series.seriesNumber ??
            series.seriesDescription ??
            (folderLastPart && folderLastPart.length <= 8 ? folderLastPart : null) ??
            "Series";
          const imageCount = series.fileCount ?? "0";
          const isSelected = series.folder ? selectedIds.has(series.folder) : false;
          return (
            <div
              key={series.folder}
              className={`case-series-expand-row__item ${isSelected ? "case-series-expand-row__item--selected" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (series.folder) {
                  toggleSelection(series.folder);
                }
              }}
            >
              <Checkbox
                checked={isSelected}
                onChange={(e) => {
                  e.stopPropagation();
                  if (series.folder) {
                    toggleSelection(series.folder);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                className="case-series-expand-row__checkbox"
              />
              {series.signedUrl ? (
                <img
                  src={series.signedUrl}
                  alt={displayName}
                  className="case-series-expand-row__thumbnail"
                />
              ) : (
                <div className="case-series-expand-row__placeholder">
                  <FileOutlined />
                </div>
              )}
              <div className="case-series-expand-row__info">
                <span className="case-series-expand-row__name">{displayName}</span>
                <span className="case-series-expand-row__count">
                  {imageCount} image{Number(imageCount) !== 1 ? "s" : ""}
                </span>
                {series.modality && (
                  <span className="case-series-expand-row__modality">
                    {series.modality}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default CaseSeriesExpandRow;
